/**
 * Overlay shown inside the CAD dialog when the designer is picking a
 * studio gallery render for one of the four multiview slots
 * (front / left / back / right). Renders a responsive grid of square
 * tiles with hover state, BOM badge, "already used" badge, and an
 * optional per-tile refine wand (when the tile has a `tripo_task_id`).
 */
import { renderImageUrl } from "@/lib/imageUrl";

export default function GalleryPicker({ slot, gallery, picks, onClose, onPick, onRefine, backendUrl }) {
  return (
    <div className="absolute inset-0 z-50 bg-black/85 backdrop-blur-md rounded-md p-4 sm:p-5 flex flex-col" data-testid="cad-gallery-picker">
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Pick {slot} view</div>
          <div className="text-base text-white">From your studio gallery</div>
        </div>
        <button
          onClick={onClose}
          className="text-white/55 hover:text-white text-xs px-2 py-1 rounded-full border border-white/10 hover:bg-white/5"
        >
          Close ✕
        </button>
      </div>
      {(!gallery || gallery.length === 0) ? (
        <p className="text-xs text-white/50">No renders in gallery yet. Create some in the studio first.</p>
      ) : (
        // `min-h-0` is critical here — without it `flex-1` doesn't actually
        // clamp the grid's height inside a flex parent in Safari/Firefox,
        // so the grid spills past the dialog edge and looks like the
        // bottom cells are clipped or overlapping. `auto-rows-max-content`
        // keeps each row at the natural height of its aspect-square
        // items so one tall (mis-rendered) cell can't pull the rest up
        // with it.
        <div
          className="flex-1 min-h-0 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 pr-1 pb-1 content-start"
          style={{ gridAutoRows: "max-content" }}
        >
          {gallery.map((g) => {
            const usedSlot = Object.entries(picks).find(([k, v]) => v === g.id && k !== slot);
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => onPick(g)}
                data-testid={`cad-picker-${g.id}`}
                // Inline `aspectRatio` wins over any conflicting browser
                // default for `<button>` elements (some Safari versions
                // ignore the Tailwind `aspect-square` class on buttons).
                // `display: block` is the second insurance — without it,
                // `aspect-ratio` falls back to `auto` on inline buttons.
                style={{ aspectRatio: "1 / 1" }}
                className="relative block w-full rounded-lg overflow-hidden border border-white/10 hover:border-[#D4AF37]/55 ring-1 ring-transparent hover:ring-[#D4AF37]/25 transition group bg-[#0A0A0A]"
                title={g.prompt}
              >
                <img
                  src={renderImageUrl(backendUrl, g)}
                  alt={g.prompt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/85 to-transparent pointer-events-none" />
                <span className="absolute bottom-1.5 left-1.5 text-[9px] uppercase tracking-[0.18em] text-[#D4AF37] font-medium leading-none">
                  {g.view && g.view !== "front" ? g.view : (g.style || "Custom")}
                </span>
                {g.materials && (
                  <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 text-[8.5px] uppercase tracking-[0.18em] text-emerald-300 bg-black/70 px-1.5 py-0.5 rounded-full border border-emerald-400/25 leading-none">
                    BOM
                  </span>
                )}
                {usedSlot && (
                  <span
                    className="absolute top-1.5 left-1.5 inline-flex items-center text-[8.5px] uppercase tracking-[0.18em] text-amber-300 bg-black/70 px-1.5 py-0.5 rounded-full border border-amber-400/30 leading-none"
                    title={`Already used as ${usedSlot[0]}`}
                  >
                    {usedSlot[0]}
                  </span>
                )}
                {onRefine && g.tripo_task_id && (
                  <span
                    onClick={(e) => { e.stopPropagation(); onRefine(g); }}
                    data-testid={`cad-picker-${g.id}-refine`}
                    title="Re-iterate this specific view (text prompt, 1 credit)"
                    className="absolute bottom-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/25 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 4V2"/><path d="M15 16v-2"/><path d="M8 9h2"/><path d="M20 9h2"/><path d="M17.8 11.8 19 13"/><path d="M15 9h.01"/><path d="M17.8 6.2 19 5"/><path d="m3 21 9-9"/><path d="M12.2 6.2 11 5"/>
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
